import React, { useCallback, useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import LoadingSpinner from '../components/LoadingSpinner';
import DeveloperNavigation, { DEV_ADMIN_EMAILS } from '../components/DeveloperNavigation';
import { billingAuthHeaders } from '../utils/billingApi';
import { surveysApiRoot } from '../utils/cancellationSurveyApi';

const thStyle = {
    textAlign: 'left',
    padding: '10px 12px',
    borderBottom: '1px solid #e5e7eb',
    background: '#f8fafc',
    position: 'sticky',
    top: 0,
    zIndex: 1,
};

const tdStyle = {
    padding: '10px 12px',
    borderBottom: '1px solid #f1f5f9',
    verticalAlign: 'top',
};

const QUESTION_LABELS = {
    nps: 'NPS',
    leave_reasons: 'Leave reasons',
    favorite_things: 'Favorites',
    least_favorite: 'Least favorite',
    tms: 'TMS',
    portal: 'Portal',
    email_assistant: 'Email assistant',
    form_generator: 'Form generator',
    other_comments: 'Other comments',
};

const SURVEY_TYPE_LABELS = {
    cancellation: 'Cancellation',
    last_free_trial: 'Last free trial',
    first_close: 'First close',
    subscriber_3mo: '3-month subscriber',
};

function truncate(value, max = 80) {
    const text = value == null ? '' : String(value);
    if (text.length <= max) return text || '—';
    return `${text.slice(0, max)}…`;
}

function formatDate(iso) {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleString();
    } catch {
        return iso;
    }
}

export default function DeveloperSurveys() {
    const { user, isAuthenticated, loginWithRedirect, getAccessTokenSilently, getIdTokenClaims } = useAuth0();
    const navigate = useNavigate();
    const [pageSize, setPageSize] = useState(20);
    const [offset, setOffset] = useState(0);
    const [surveys, setSurveys] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [tableLoading, setTableLoading] = useState(false);
    const [error, setError] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [typeFilter, setTypeFilter] = useState('');
    const [emailFilter, setEmailFilter] = useState('');
    const [userIdFilter, setUserIdFilter] = useState('');
    const [expandedId, setExpandedId] = useState(null);
    const [questionKeys, setQuestionKeys] = useState(Object.keys(QUESTION_LABELS));

    const isDevAdmin = DEV_ADMIN_EMAILS.includes((user?.email || '').toLowerCase());

    const fetchSurveys = useCallback(async (resetOffset = true, startOffset = 0, filters) => {
        const nextOffset = resetOffset ? 0 : startOffset;
        const status = filters ? filters.status : statusFilter;
        const surveyType = filters ? filters.surveyType : typeFilter;
        const email = filters ? filters.email : emailFilter;
        const userId = filters ? filters.userId : userIdFilter;
        try {
            if (resetOffset) setLoading(true);
            else setTableLoading(true);
            const headers = await billingAuthHeaders({ getAccessTokenSilently, getIdTokenClaims });
            const params = { limit: pageSize, offset: nextOffset };
            if (status) params.status = status;
            if (surveyType) params.survey_type = surveyType;
            if ((email || '').trim()) params.email = email.trim();
            if ((userId || '').trim()) params.user_id = userId.trim();
            const res = await axios.get(`${surveysApiRoot()}/admin`, { headers, params });
            const rows = res.data.surveys || [];
            const keys = res.data.question_keys;
            if (Array.isArray(keys) && keys.length) {
                setQuestionKeys(keys);
            }
            setSurveys((prev) => (resetOffset ? rows : [...prev, ...rows]));
            setTotalCount(res.data.total_count || 0);
            setOffset(resetOffset ? 0 : nextOffset + rows.length);
            setError('');
        } catch (err) {
            setError(err.response?.data?.error || err.message);
        } finally {
            setLoading(false);
            setTableLoading(false);
        }
    }, [emailFilter, getAccessTokenSilently, getIdTokenClaims, pageSize, statusFilter, typeFilter, userIdFilter]);

    useEffect(() => {
        if (isAuthenticated && isDevAdmin) {
            fetchSurveys(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated, isDevAdmin, pageSize]);

    if (!isAuthenticated) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', flexDirection: 'column' }}>
                <p>Please log in</p>
                <button onClick={() => loginWithRedirect({ authorizationParams: { prompt: 'login' } })}>Log in</button>
            </div>
        );
    }

    if (!isDevAdmin) {
        return (
            <div style={{ padding: '24px' }}>
                <h2>Access Denied</h2>
                <p>This page is restricted to authorized KeyFlo developers.</p>
            </div>
        );
    }

    if (loading) return <LoadingSpinner />;

    return (
        <div style={{ padding: '24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                <h2>Developer • Surveys</h2>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', position: 'relative' }}>
                    <button className="back-button" onClick={() => navigate('/')}>← Back to Contracts</button>
                    <label style={{ marginRight: '8px' }}>Page size:</label>
                    <select value={pageSize} onChange={(e) => setPageSize(parseInt(e.target.value, 10))}>
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                    </select>
                    <DeveloperNavigation />
                </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: '12px', marginBottom: '12px', maxWidth: '1100px' }}>
                <div>
                    <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '4px' }}>Status</label>
                    <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={{ width: '100%' }}>
                        <option value="">All</option>
                        <option value="pending">pending</option>
                        <option value="in_progress">in_progress</option>
                        <option value="completed">completed</option>
                        <option value="expired">expired</option>
                    </select>
                </div>
                <div>
                    <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '4px' }}>Type</label>
                    <select
                        value={typeFilter}
                        onChange={(e) => {
                            const nextType = e.target.value;
                            setTypeFilter(nextType);
                            fetchSurveys(true, 0, {
                                status: statusFilter,
                                surveyType: nextType,
                                email: emailFilter,
                                userId: userIdFilter,
                            });
                        }}
                        style={{ width: '100%' }}
                    >
                        <option value="">All</option>
                        {Object.entries(SURVEY_TYPE_LABELS).map(([value, label]) => (
                            <option key={value} value={value}>{label}</option>
                        ))}
                    </select>
                </div>
                <div>
                    <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '4px' }}>User Email</label>
                    <input value={emailFilter} onChange={(e) => setEmailFilter(e.target.value)} placeholder="email" style={{ width: '100%' }} />
                </div>
                <div>
                    <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '4px' }}>User ID</label>
                    <input value={userIdFilter} onChange={(e) => setUserIdFilter(e.target.value)} placeholder="user id" style={{ width: '100%' }} />
                </div>
            </div>

            <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
                <button onClick={() => fetchSurveys(true)}>Apply Filters</button>
                <button onClick={() => {
                    setStatusFilter('');
                    setTypeFilter('');
                    setEmailFilter('');
                    setUserIdFilter('');
                    setOffset(0);
                    fetchSurveys(true, 0, { status: '', surveyType: '', email: '', userId: '' });
                }}>Clear</button>
                <button
                    onClick={() => fetchSurveys(false, surveys.length)}
                    disabled={surveys.length >= totalCount}
                >
                    Load More
                </button>
            </div>

            {tableLoading && <LoadingSpinner />}
            {error && <div style={{ color: 'red', marginBottom: '12px' }}>{error}</div>}

            <div style={{ marginBottom: '12px' }}>
                Showing {surveys.length} of {totalCount} surveys
            </div>

            <div style={{ overflowX: 'auto', background: '#ffffff', border: '1px solid #e5e7eb' }}>
                <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
                    <thead>
                        <tr>
                            <th style={thStyle}>User ID</th>
                            <th style={thStyle}>Email</th>
                            <th style={thStyle}>Name</th>
                            <th style={thStyle}>Type</th>
                            <th style={thStyle}>Status</th>
                            <th style={thStyle}>Started</th>
                            <th style={thStyle}>Completed</th>
                            {questionKeys.map((key) => (
                                <th key={key} style={thStyle}>{QUESTION_LABELS[key] || key}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {surveys.map((row) => (
                            <React.Fragment key={row.id}>
                                <tr>
                                    <td style={tdStyle}>{row.user_id}</td>
                                    <td style={tdStyle}>{row.email || '—'}</td>
                                    <td style={tdStyle}>{row.name || '—'}</td>
                                    <td style={tdStyle}>{SURVEY_TYPE_LABELS[row.survey_type] || row.survey_type || '—'}</td>
                                    <td style={tdStyle}>{row.status}</td>
                                    <td style={tdStyle}>{formatDate(row.started_at)}</td>
                                    <td style={tdStyle}>{formatDate(row.completed_at)}</td>
                                    {questionKeys.map((key) => (
                                        <td
                                            key={key}
                                            style={{ ...tdStyle, cursor: 'pointer' }}
                                            onClick={() => setExpandedId(expandedId === row.id ? null : row.id)}
                                        >
                                            {truncate(row.answers?.[key])}
                                        </td>
                                    ))}
                                </tr>
                                {expandedId === row.id && (
                                    <tr>
                                        <td colSpan={7 + questionKeys.length} style={{ ...tdStyle, background: '#f8fafc' }}>
                                            <div style={{ marginBottom: '8px' }}>
                                                <strong>Type:</strong> {SURVEY_TYPE_LABELS[row.survey_type] || row.survey_type || '—'}
                                            </div>
                                            {questionKeys.map((key) => (
                                                <div key={key} style={{ marginBottom: '8px' }}>
                                                    <strong>{QUESTION_LABELS[key] || key}:</strong> {row.answers?.[key] || '—'}
                                                </div>
                                            ))}
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        ))}
                        {surveys.length === 0 && !tableLoading && (
                            <tr>
                                <td colSpan={7 + questionKeys.length} style={{ padding: '12px', textAlign: 'center' }}>No surveys found</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
